import {observer} from 'mobx-react';
import '../geography-filter.scss';
import {ReactNode, useContext, useEffect, useMemo} from 'react';
import {NetworkContext} from '../../../lib/context';
import {FilterProps} from '../filter-types';
import FilterContainer from '../filter-container';

const DisruptionAnalysisFilter = observer(() => {
    const noStore = useContext(NetworkContext);

    useEffect(() => {
        const controller = new AbortController();

        if (noStore.runId &&
            noStore.availableDisruptionAnalysisFilters.length === 0) {
            noStore.loadDisruptiveAnalysisFilterData(controller).catch((err) => {
                noStore.handleApiError(err, 'Failed to Load Disruption Analysis Filters', () => noStore.loadDisruptiveAnalysisFilterData());
            });
        }

        return () => {
            controller.abort();
        };
    }, [noStore.runId]);

    const lineOfBusinessFilter: FilterProps = useMemo(() => {
        const data = noStore.getGeoFilterUniqueValuesByKey('DISRUPTION_ANALYSIS', 'standard_type');
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisFilter('standard_type', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        return {
            label: 'Line of Business',
            data,
            value: noStore.appliedDisruptionAnalysisFilter.standard_type,
            onChange,
            children,
            spinner: noStore.loadingDisruptionAnalysisData,
            ['data-testid']: 'line-of-business-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingDisruptionAnalysisData,
        noStore.availableDisruptionAnalysisFilters,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
    ]);

    const patientRosterFilter: FilterProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedDisruptionAnalysisFilter.standard_type,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'DISRUPTION_ANALYSIS',
            'patient_roster_name',
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisFilter('patient_roster_name', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        return {
            label: 'Patient Roster',
            data,
            value: noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
            onChange,
            children,
            spinner: noStore.loadingDisruptionAnalysisData,
            ['data-testid']: 'patient-roster-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingDisruptionAnalysisData,
        noStore.availableDisruptionAnalysisFilters,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
        noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
    ]);

    const stateFilter: FilterProps = useMemo(() => {
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'DISRUPTION_ANALYSIS',
            'state_additional_info.state',
        );
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisFilter('state', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        return {
            label: 'State',
            data,
            value: noStore.appliedDisruptionAnalysisFilter.state,
            onChange,
            children,
            spinner: noStore.loadingDisruptionAnalysisData,
            ['data-testid']: 'state-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingDisruptionAnalysisData,
        noStore.availableDisruptionAnalysisFilters,
        noStore.appliedDisruptionAnalysisFilter.state,
        noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
    ]);

    const countyFilter: FilterProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedDisruptionAnalysisFilter.standard_type,
            'state_additional_info.state': noStore.appliedDisruptionAnalysisFilter.state,
            patient_roster_name: noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'DISRUPTION_ANALYSIS',
            ['county_additional_info.county_name', 'ssacd'],
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisFilter('county', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        const disabled =
            !noStore.appliedDisruptionAnalysisFilter.state ||
            !noStore.appliedDisruptionAnalysisFilter.patient_roster_name;
        return {
            label: 'County',
            data,
            value: noStore.appliedDisruptionAnalysisFilter.county,
            onChange,
            children,
            spinner: noStore.loadingDisruptionAnalysisData,
            disabled,
            ['data-testid']: 'county-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingDisruptionAnalysisData,
        noStore.availableDisruptionAnalysisFilters,
        noStore.appliedDisruptionAnalysisFilter.state,
        noStore.appliedDisruptionAnalysisFilter.county,
        noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
    ]);

    const zipFilter: FilterProps = useMemo(() => {
        const geoFilterArgs = {
            ssacd: noStore.disruptionAnalysisFilters.county,
            'state_additional_info.state': noStore.appliedDisruptionAnalysisFilter.state,
            patient_roster_name: noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
            standard_type: noStore.appliedDisruptionAnalysisFilter.standard_type,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'DISRUPTION_ANALYSIS',
            'zip',
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisFilter('zip', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];
        const disabled =
            !noStore.appliedDisruptionAnalysisFilter.state ||
            !noStore.appliedDisruptionAnalysisFilter.patient_roster_name ||
            !noStore.appliedDisruptionAnalysisFilter.county;
        return {
            label: 'ZIP',
            data,
            value: noStore.appliedDisruptionAnalysisFilter.zip,
            onChange,
            children,
            spinner: noStore.loadingDisruptionAnalysisData,
            disabled,
            ['data-testid']: 'zip-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingDisruptionAnalysisData,
        noStore.availableDisruptionAnalysisFilters,
        noStore.appliedDisruptionAnalysisFilter.state,
        noStore.appliedDisruptionAnalysisFilter.county,
        noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
        noStore.appliedDisruptionAnalysisFilter.zip,
    ]);

    const specialtyFilter: FilterProps = useMemo(() => {
        const source = noStore.disruptionAnalysisComplianceScores;

        const getSpecialtyName = (code: string): string => {
            const specialty = source.find(s => s['specialty_code'] === code);
            return specialty ? specialty['Specialty'] : code;
        };

        const data = source.map((score) => score['specialty_code']).filter(Boolean);
        const onChange = (e: string) => {
            noStore.setDisruptionAnalysisSpecialtyFilter(e);
        };
        const children: [(item: any) => string, (item: any) => ReactNode] = [
            (item: any) => item,
            (item: any) => getSpecialtyName(item) || 'All',
        ];
        const disabled = !noStore.appliedDisruptionAnalysisFilter.standard_type;
        return {
            label: 'Specialty',
            data,
            value: noStore.disruptionAnalysisSpecialtyFilter,
            onChange,
            children,
            spinner: noStore.loadingDisruptionAnalysisSpecialtyData,
            disabled,
            itemToString: getSpecialtyName,
            ['data-testid']: 'specialty-dropdown',
        };
    }, [
        noStore.runId,
        noStore.disruptionAnalysisComplianceScores,
        noStore.disruptionAnalysisSpecialtyFilter,
        noStore.loadingDisruptionAnalysisSpecialtyData,
        noStore.appliedDisruptionAnalysisFilter.county,
        noStore.appliedDisruptionAnalysisFilter.state,
        noStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        noStore.appliedDisruptionAnalysisFilter.standard_type,
    ]);

    useEffect(() => {
        if (!noStore.appliedDisruptionAnalysisFilter.standard_type) {
            noStore.setDisruptionAnalysisSpecialtyFilter('');
        }
    }, [noStore.appliedDisruptionAnalysisFilter.standard_type]);

    const filterConfiguration: Array<FilterProps> = [
        lineOfBusinessFilter,
        patientRosterFilter,
        stateFilter,
        countyFilter,
        zipFilter,
        specialtyFilter,
    ];

    return (
        <FilterContainer
            filterConfiguration={filterConfiguration}
        />
    );
});

export default DisruptionAnalysisFilter;
